import type {
  RouteRecordRaw,
  Router,
  RouteComponent,
  LocationQuery,
} from 'vue-router';
import { isEqual } from 'lodash';

import type { Serializer } from '@libs/utils';
import { log, assertValue } from '@libs/utils';

type QueryDictionary =
  | LocationQuery
  | Record<string, string | Array<string | null>>;

const INGREDIENT_QUERY_KEY_PATTERN = /^i\d+$/;

export interface QueryHandlerOptions<T> {
  hasDefaultValue?: (value: T) => boolean;
  ignoreEmptyQueryParameters?: boolean;
}

function toArrayOptions<T>(
  options?: QueryHandlerOptions<T>
): QueryHandlerOptions<T[]> | undefined {
  if (options === undefined) {
    return undefined;
  }

  const newOptions: QueryHandlerOptions<T[]> = {};

  if (options.ignoreEmptyQueryParameters) {
    newOptions.ignoreEmptyQueryParameters = options.ignoreEmptyQueryParameters;
  }

  // NOTE: Code line inside if statement is copied from frontend-libraries.
  // It works, but it contains eslint / type errors.
  // I think this line is equivalent of this code:
  // const hasDefaultValue = options?.hasDefaultValue;
  // if (hasDefaultValue) {
  //   newOptions.hasDefaultValue = (value): boolean => {
  //     const firstFromArray = value[0];
  //     if (firstFromArray) {
  //       return hasDefaultValue(firstFromArray);
  //     }
  //     return true;
  //   };
  // }
  // but I'm not willing to risk breaking applicaiton by introducing it.

  if (options?.hasDefaultValue) {
    // eslint-disable-next-line
    // @ts-ignore
    // eslint-disable-next-line
    newOptions.hasDefaultValue = (val) => options.hasDefaultValue!(val[0]);
  }

  return newOptions;
}

interface Handler<TQuery, TProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serializer: Serializer<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propsSetter: (value: any[], props: TProps) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryGetter: (value: TQuery) => any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: Required<QueryHandlerOptions<any>>;
}

export class RouteHandler<TQuery, TProps> implements RouteHandler<
  TQuery,
  TProps
> {
  private readonly handlers: Record<string, Handler<TQuery, TProps>> = {};
  private patternHandler: Handler<TQuery, TProps> | undefined = undefined;
  private pattenHandlerRegExp: RegExp | undefined = undefined;

  constructor(private propsFactory: () => TProps) {}

  public registerHandler<T>(
    queryPropertyName: string,
    serializer: Serializer<T>,
    propsSetter: (value: T, props: TProps) => void,
    queryGetter: (query: TQuery) => T,
    options?: QueryHandlerOptions<T>
  ): void {
    this.registerArrayHandler(
      queryPropertyName,
      serializer,
      (value, p) => {
        if (value[0] === undefined) {
          throw new Error('router prop value not found');
        }

        return propsSetter(value[0], p);
      },

      (value) => [queryGetter(value)],
      toArrayOptions(options)
    );
  }

  public registerArrayPatternHandler<T>(
    pattern: RegExp,
    serializer: Serializer<T>,
    propsSetter: (value: T[], props: TProps) => void,
    queryGetter: (query: TQuery) => T[],
    options?: QueryHandlerOptions<T[]>
  ): void {
    this.pattenHandlerRegExp = pattern;
    this.patternHandler = {
      serializer,
      propsSetter,
      queryGetter,
      options: {
        ignoreEmptyQueryParameters: false,
        hasDefaultValue: (): boolean => false,
        ...options,
      },
    };
  }

  public registerArrayHandler<T>(
    queryPropertyName: string,
    serializer: Serializer<T>,
    propsSetter: (value: T[], props: TProps) => void,
    queryGetter: (query: TQuery) => T[],
    options?: QueryHandlerOptions<T[]>
  ): void {
    this.handlers[queryPropertyName] = {
      serializer,
      propsSetter,
      queryGetter,
      options: {
        ignoreEmptyQueryParameters: false,
        hasDefaultValue: (): boolean => false,
        ...options,
      },
    };
  }

  public getProps(query: QueryDictionary): TProps {
    const result = this.propsFactory();

    for (const key of Object.keys(this.handlers)) {
      if (key in query) {
        const handler = this.handlers[key];
        assertValue(handler, 'router.ts > getProps > handler');
        const queryValue = query[key];
        let stringValues: string[] = [];

        if (typeof queryValue === 'string') {
          stringValues = [queryValue];
        } else {
          stringValues = queryValue as string[];
        }

        const shouldIgnoreParameter =
          handler.options.ignoreEmptyQueryParameters &&
          (stringValues.length === 0 ||
            (stringValues.length === 1 && stringValues[0] === ''));

        if (!shouldIgnoreParameter) {
          const values = stringValues.map((value) =>
            handler.serializer.deserialize(value)
          );
          handler.propsSetter(values, result);
        }
      }
    }

    const ingredientKeys = Object.keys(query).filter((key) => {
      if (this.pattenHandlerRegExp) {
        return key.match(this.pattenHandlerRegExp);
      }
      return false;
    });

    if (ingredientKeys.length > 0) {
      const handler = this.patternHandler;
      assertValue(handler, 'router.ts > getProps > handler');
      const queryValues = ingredientKeys.map((key) => query[key]) as string[];
      const values = queryValues.map((value) =>
        handler.serializer.deserialize(value)
      );
      handler.propsSetter(values, result);
    }

    return result;
  }

  public getQuery(
    query: TQuery,
    currentQuery: QueryDictionary
  ): QueryDictionary {
    const result = {
      ...currentQuery,
    };

    const keysWithoutOldIngredientKey = Object.keys(this.handlers).filter(
      (key: string) => key !== 'i'
    );

    for (const key of keysWithoutOldIngredientKey) {
      const handler = this.handlers[key];
      assertValue(handler, 'router.ts > getQuery > handler');
      const keyValues: string[] = [];
      const values = handler.queryGetter(query);

      if (handler.options.hasDefaultValue(values)) {
        delete result[key];
      } else {
        for (const value of values) {
          keyValues.push(handler.serializer.serialize(value));
        }

        result[key] = keyValues;
      }
    }

    if (this.patternHandler) {
      Object.keys(result).forEach((key) => {
        if (INGREDIENT_QUERY_KEY_PATTERN.test(key)) {
          delete result[key];
        }
      });

      const handler = this.patternHandler;
      assertValue(handler, 'router.ts > getQuery > handler');
      const keyValues: string[] = [];
      const values = handler.queryGetter(query);

      values.forEach((value) =>
        keyValues.push(handler.serializer.serialize(value))
      );

      keyValues.forEach(
        (keyValue, index) => (result[`i${index + 1}`] = keyValue)
      );
    }

    return result;
  }
}

export interface RouteBuilder {
  withName(name: string): NamedRouteBuilder;
}

export interface NamedRouteBuilder {
  withPath(path: string): ComponentRouteBuilder;
}

export type RootRouteBuilder = RouteBuilder & NamedRouteBuilder;

export interface ChildrenRouteBuilder {
  withChildren(children: RouteRecordRaw[]): PropsRouteBuilder & Buildable;
}

export interface ComponentRouteBuilder {
  useComponent(
    component: RouteComponent
  ): ChildrenRouteBuilder & PropsRouteBuilder & Buildable;
}

export interface PropsRouteBuilder {
  hasProps<T>(propsFactory: () => T): QueryMappingBuilder<T>;
}

export interface QueryMappingBuilder<TProps> {
  mappedToQuery<T>(
    mapper: (mapping: QueryMapping<T, TProps>) => QueryMapping<T, TProps>
  ): BuildableWithProps<T>;
}

export interface QueryMapping<TQuery, TProps> {
  property<T>(
    queryPropertyName: string,
    serializer: Serializer<T>
  ): ToArrayPropertyMapping<T, TQuery, TProps> &
    QueryPropertyMapping<T, TQuery, TProps>;
  propertyPattern<T>(
    pattern: RegExp,
    serializer: Serializer<T>
  ): ToArrayPropertyMapping<T, TQuery, TProps>;
}

export interface ToArrayPropertyMapping<T, TQuery, TProps> {
  array(): QueryPropertyMapping<T[], TQuery, TProps>;
}

export interface QueryPropertyMapping<T, TQuery, TProps> {
  ignoreEmptyQueryParameters(): QueryPropertyMapping<T, TQuery, TProps>;
  with(
    config: QueryPropertyConfig<T, TQuery, TProps>
  ): QueryMapping<TQuery, TProps>;
}

export const Route: () => RootRouteBuilder = () => new BuilderImplementation();

export interface QueryPropertyConfig<T, TQuery, TProps> {
  setter: (value: T, props: TProps) => void;
  getter: (query: TQuery) => T;
  hasDefaultValue?: (value: T) => boolean;
}

interface BuiltRoute {
  config: RouteRecordRaw;
}

interface Buildable {
  build(): BuiltRoute;
}

export interface BuiltRouteWithProps<TQuery> extends BuiltRoute {
  setQuery(query: TQuery, router: Router): Promise<void>;
}

export interface BuildableWithProps<TQuery> {
  build(): BuiltRouteWithProps<TQuery>;
}

class BuilderImplementation
  implements
    RootRouteBuilder,
    ComponentRouteBuilder,
    PropsRouteBuilder,
    Buildable
{
  private path?: string;
  private name?: string;
  private component?: RouteComponent;
  private children?: RouteRecordRaw[];

  public get config(): RouteRecordRaw {
    return {
      props: {},
      path: this.path,
      name: this.name,
      component: this.component,
      children: this.children,
    } as RouteRecordRaw;
  }

  public build(): BuiltRoute {
    return this;
  }

  public withName(name: string): NamedRouteBuilder {
    this.name = name;
    return this;
  }

  public withChildren(
    children: RouteRecordRaw[]
  ): PropsRouteBuilder & Buildable {
    this.children = children;
    return this;
  }

  public withPath(path: string): ComponentRouteBuilder {
    this.path = path;
    if (!this.name) {
      this.name = path;
    }
    return this;
  }

  public useComponent(
    component: RouteComponent
  ): PropsRouteBuilder & Buildable & ChildrenRouteBuilder {
    this.component = component;
    return this;
  }

  public hasProps<T>(propsFactory: () => T): QueryMappingBuilder<T> {
    return new QueryBuilder<T>(this, propsFactory);
  }
}

class QueryBuilder<TProps> implements QueryMappingBuilder<TProps> {
  constructor(
    private builder: BuilderImplementation,
    private propsFactory: () => TProps
  ) {}

  public mappedToQuery<T>(
    mapper: (mapping: QueryMapping<T, TProps>) => QueryMapping<T, TProps>
  ): BuildableWithProps<T> {
    const handler = new RouteHandler<T, TProps>(this.propsFactory);
    const mapObject = new QueryMappingImpl<T, TProps>(this.builder, handler);
    mapper(mapObject);
    return mapObject;
  }
}

class QueryMappingImpl<TQuery, TProps>
  implements QueryMapping<TQuery, TProps>, BuildableWithProps<TQuery>
{
  private pendingQuery: QueryDictionary | undefined;
  constructor(
    private builder: BuilderImplementation,
    private handler: RouteHandler<TQuery, TProps>
  ) {}

  public build(): BuiltRouteWithProps<TQuery> {
    const route = this.builder.build().config;

    if ('props' in route) {
      route.props = (route): TProps => this.handler.getProps(route.query);
    }

    return {
      config: route,
      setQuery: async (query: TQuery, router: Router): Promise<void> => {
        log({ title: 'Update query', context: [query] });
        try {
          const newQuery = this.handler.getQuery(
            query,
            router.currentRoute.value.query
          );

          // The old `i` param is already in the recipe that getQuery serialized
          // into i1..iN, so copying it over i1 duplicated that ingredient.
          delete newQuery.i;

          const currentRoute = router.currentRoute.value;

          const resolvedPath = router.resolve({
            path: currentRoute.path,
            query: newQuery,
            hash: currentRoute.hash,
          }).fullPath;

          // Decode both sides to normalise encoding differences
          // (e.g. %3A vs : for colon — vue-router leaves : unencoded while
          // some external URL sources encode it). Semantically identical URLs
          // must compare equal so we don't fire a redundant router.replace().
          const urlWouldChange =
            decodeURIComponent(resolvedPath) !==
            decodeURIComponent(currentRoute.fullPath);

          // Skip only while the write is in flight: currentRoute keeps the old
          // url until it commits, so an overlapping flush with the same query
          // would abort that navigation. Remembering the last written query
          // instead would also skip resyncs when the url changed elsewhere.
          if (urlWouldChange && !isEqual(newQuery, this.pendingQuery)) {
            this.pendingQuery = newQuery;
            try {
              await router.replace({
                query: newQuery,
                hash: currentRoute.hash,
              });
            } finally {
              this.pendingQuery = undefined;
            }
          }
        } catch (error) {
          log({ title: 'Route error', context: [error], level: 'error' });
        }
      },
    };
  }

  public propertyPattern<T>(
    pattern: RegExp,
    serializer: Serializer<T>
  ): ToArrayPropertyMapping<T, TQuery, TProps> {
    let ignoreEmptyQueryParameters = false;
    return {
      array: () => ({
        with: (config): typeof this => {
          this.handler.registerArrayPatternHandler(
            pattern,
            serializer,
            config.setter,
            config.getter,
            {
              ignoreEmptyQueryParameters,
              hasDefaultValue: config.hasDefaultValue,
            }
          );

          return this;
        },
        ignoreEmptyQueryParameters(): QueryPropertyMapping<
          T[],
          TQuery,
          TProps
        > {
          ignoreEmptyQueryParameters = true;
          return this;
        },
      }),
    };
  }

  public property<T>(
    queryPropertyName: string,
    serializer: Serializer<T>
  ): ToArrayPropertyMapping<T, TQuery, TProps> &
    QueryPropertyMapping<T, TQuery, TProps> {
    let ignoreEmptyQueryParameters = false;
    return {
      array: () => ({
        with: (config): typeof this => {
          this.handler.registerArrayHandler(
            queryPropertyName,
            serializer,
            config.setter,
            config.getter,
            {
              ignoreEmptyQueryParameters,
              hasDefaultValue: config.hasDefaultValue,
            }
          );

          return this;
        },
        ignoreEmptyQueryParameters(): QueryPropertyMapping<
          T[],
          TQuery,
          TProps
        > {
          ignoreEmptyQueryParameters = true;
          return this;
        },
      }),
      with: (config): typeof this => {
        this.handler.registerHandler(
          queryPropertyName,
          serializer,
          config.setter,
          config.getter,
          {
            ignoreEmptyQueryParameters,
            hasDefaultValue: config.hasDefaultValue,
          }
        );
        return this;
      },
      ignoreEmptyQueryParameters(): ToArrayPropertyMapping<T, TQuery, TProps> &
        QueryPropertyMapping<T, TQuery, TProps> {
        ignoreEmptyQueryParameters = true;
        return this;
      },
    };
  }
}
